import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/shell/PageHeader';
import DataTable from '../../components/list/DataTable';
import { SavedViewTabs, FilterBar } from '../../components/list/ListScreen';
import UserCell from '../../components/ui/UserCell';
import Modal from '../../components/overlays/Modal';
import { Field, Select } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  generatePayout,
  getPayoutDetail,
  getPayoutDetailMonthWise,
  getPayoutSummary,
  getPayoutSummaryDataByMonth,
} from '../../actions/agentPayout';
import { inr, number } from '../../helpers/format';
import './partners.css';

const VIEWS = [
  { key: 'due', label: 'By partner' },
  { key: 'paid', label: 'By month' },
];

/** Last 12 months as YYYY-MM, newest first. */
function recentMonths(count = 12) {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({
      value,
      label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
    });
  }
  return out;
}

export default function Payouts() {
  const { confirm, toast } = useOverlays();
  const [view, setView] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(recentMonths()[0].value);
  const [generating, setGenerating] = useState(false);
  const [genMonth, setGenMonth] = useState(recentMonths()[0].value);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState([]);

  const months = useMemo(() => recentMonths(), []);
  const byPartner = view === 0;

  const load = useCallback(async () => {
    setLoading(true);
    setExpanded(null);
    try {
      const data = byPartner
        ? await getPayoutSummary({ start_month_year: month })
        : await getPayoutSummaryDataByMonth({ start_month_year: month });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [byPartner, month]);

  useEffect(() => {
    load();
  }, [load]);

  /** Expanding a row pulls that partner's or month's individual payout records. */
  const expand = useCallback(
    async (row) => {
      const key = byPartner ? row.agent_id : row.month_year;
      if (expanded === key) {
        setExpanded(null);
        return;
      }
      setExpanded(key);
      setDetail([]);
      try {
        const data = byPartner
          ? await getPayoutDetail({ start_month_year: month, agent_id: row.agent_id })
          : await getPayoutDetailMonthWise({ start_month_year: month });
        setDetail(Array.isArray(data) ? data : []);
      } catch {
        setDetail([]);
      }
    },
    [byPartner, expanded, month]
  );

  const runGeneration = useCallback(async () => {
    const label = months.find((m) => m.value === genMonth)?.label || genMonth;
    const ok = await confirm({
      title: `Generate payouts for ${label}?`,
      body: `This creates a payout record and PDF statement for every partner with completed sessions in ${label}. It can only be done once per month.`,
      confirmLabel: 'Generate',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await generatePayout(genMonth);
      toast(res?.message || 'Payouts generated', { tone: 'success' });
      setGenerating(false);
      load();
    } finally {
      setBusy(false);
    }
  }, [confirm, genMonth, months, toast, load]);

  const columns = useMemo(
    () =>
      byPartner
        ? [
            {
              key: 'agent_name',
              header: 'Partner',
              width: 260,
              render: (r) => <UserCell name={r.agent_name || '—'} secondary={r.agent_email} seed={r.agent_id} />,
            },
            { key: 'total_payout', header: 'Gross earnings', width: 160, mono: true, align: 'right', render: (r) => inr(r.total_payout) },
            { key: 'total_sessions', header: 'Sessions', width: 120, mono: true, align: 'right', render: (r) => number(r.total_sessions) },
            { key: 'total_tds', header: 'TDS', width: 120, mono: true, align: 'right', render: (r) => inr(r.total_tds) },
          ]
        : [
            { key: 'month_year', header: 'Month', width: 200, render: (r) => r.month_year },
            { key: 'total_payout', header: 'Total released', width: 180, mono: true, align: 'right', render: (r) => inr(r.total_payout) },
            { key: 'total_sessions', header: 'Sessions', width: 140, mono: true, align: 'right', render: (r) => number(r.total_sessions) },
            { key: 'total_tds', header: 'TDS', width: 140, mono: true, align: 'right', render: (r) => inr(r.total_tds) },
          ],
    [byPartner]
  );

  const totals = rows.reduce(
    (acc, r) => ({
      payout: acc.payout + (r.total_payout || 0),
      sessions: acc.sessions + (r.total_sessions || 0),
    }),
    { payout: 0, sessions: 0 }
  );

  return (
    <>
      <PageHeader
        section="Partners"
        title="Payouts"
        subtitle="What each partner has earned, and what has already been released."
        primaryAction={{ label: 'Generate payout', icon: 'bx-plus', onClick: () => setGenerating(true) }}
      />

      <div className="sv-screen-body">
        <SavedViewTabs views={VIEWS} activeView={view} onChange={setView} counts={{}} />

        <FilterBar
          filters={[{ key: 'month', label: 'Month', options: months, placeholder: 'Choose a month' }]}
          values={{ month }}
          onChange={(_, value) => setMonth(value)}
        />

        {!loading && rows.length > 0 && (
          <div className="sv-payout-totals">
            <span>
              <strong>{inr(totals.payout)}</strong> total
            </span>
            <span className="sv-ai-messages__rule" />
            <span>
              <strong>{number(totals.sessions)}</strong> sessions
            </span>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => (byPartner ? r.agent_id : r.month_year)}
          loading={loading}
          selectable={false}
          onRowClick={expand}
          noun="payouts"
          empty={{
            icon: 'bx-wallet',
            title: 'No payouts for this month',
            description: 'Generate payouts once the month has closed.',
          }}
        />

        {expanded && (
          <div className="sv-card sv-payout-detail">
            <h3 className="sv-card__title">Payout records</h3>
            {detail.length === 0 && <p className="sv-payout-detail__empty">No individual records.</p>}
            {detail.map((d) => (
              <div className="sv-payout-detail__row" key={d.payout_id}>
                <span>{d.month_year}</span>
                <span className="sv-mono">{inr(d.total_payment)}</span>
                <span className="sv-mono">{number(d.total_sessions)} sessions</span>
                <span>{d.status?.replace(/_/g, ' ')}</span>
                {d.pdf_url && (
                  <a href={d.pdf_url} target="_blank" rel="noopener noreferrer">
                    Statement
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={generating}
        onClose={() => setGenerating(false)}
        title="Generate payouts"
        confirmLabel="Continue"
        confirmLoading={busy}
        onConfirm={runGeneration}
      >
        <p style={{ marginTop: 0 }}>
          Payouts can only be generated once a month has closed, and only once per month.
        </p>
        <Field label="Payout month">
          <Select options={months} value={genMonth} onChange={(e) => setGenMonth(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}
